import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
// En tu proyecto real, asegúrate de que esta ruta sea correcta.
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Stack,
    CircularProgress, Alert, Tooltip, IconButton, Dialog, DialogActions,
    DialogContent, DialogTitle, Snackbar, DialogContentText,
    Autocomplete,
    InputAdornment // <-- ADDED THIS IMPORT
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';

// --- INTERFACES ---
interface Campaign {
    id: number;
    name: string;
}

// --- COMPONENTE PRINCIPAL ---
const UpdateCostPage: React.FC = () => {
    // --- ESTADOS ---
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    
    // Estados del formulario
    const [selectedCampaignId, setSelectedCampaignId] = useState<number | ''>('');
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');
    const [newCpc, setNewCpc] = useState<string>('');

    const [confirmOpen, setConfirmOpen] = useState(false);

    // --- LÓGICA DE DATOS ---
    const fetchCampaigns = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/campaigns`);
            setCampaigns(response.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error al cargar las campañas.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchCampaigns();
    }, [fetchCampaigns]);

    // --- MANEJADORES DE EVENTOS ---
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedCampaignId || !startDate || !endDate || !newCpc) {
            setNotification({ type: 'error', message: 'Todos los campos son obligatorios.' });
            return;
        }
        setConfirmOpen(true);
    };

    const handleConfirmUpdate = async () => {
        setConfirmOpen(false);
        setIsUpdating(true);
        try {
            const payload = {
                campaign_id: selectedCampaignId,
                startDate,
                endDate,
                new_cpc: parseFloat(newCpc)
            };
            const response = await axios.post(`${API_BASE_URL}/tools/update-click-cost`, payload);
            setNotification({ type: 'success', message: response.data.message || 'Costos actualizados con éxito.' });
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al actualizar los costos.' });
        } finally {
            setIsUpdating(false);
        }
    };

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={6000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
                <BuildCircleIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Herramienta de Corrección de Costos
            </Typography>

            <Paper elevation={3} sx={{ p: 3, borderRadius: '12px' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Esta herramienta te permite recalcular el costo de los clics para una campaña dentro de un rango de fechas específico. Úsala con cuidado, ya que esta acción es irreversible.
                </Typography>
                <Box component="form" onSubmit={handleSubmit} noValidate>
                    <Stack spacing={3}>
                        <Autocomplete
                            options={campaigns}
                            getOptionLabel={(option) => `(${option.id}) ${option.name}`}
                            value={campaigns.find(c => c.id === selectedCampaignId) || null}
                            onChange={(event, newValue) => {
                                setSelectedCampaignId(newValue ? newValue.id : '');
                            }}
                            renderInput={(params) => <TextField {...params} label="Seleccionar Campaña" required />}
                        />
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                            <TextField fullWidth label="Desde" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} required />
                            <TextField fullWidth label="Hasta" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} required />
                        </Stack>
                        <TextField
                            fullWidth
                            label="Nuevo CPC"
                            type="number"
                            value={newCpc}
                            onChange={e => setNewCpc(e.target.value)}
                            required
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { step: "0.0001" } 
                            }}
                            helperText="Introduce el costo por clic correcto. Ej: 0.003"
                        />
                        <Button type="submit" variant="contained" color="primary" disabled={isUpdating}>
                            {isUpdating ? <CircularProgress size={24} /> : 'Actualizar Costos'}
                        </Button>
                    </Stack>
                </Box>
            </Paper>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar Actualización de Costos</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Estás a punto de actualizar el costo de **todos los clics** para la campaña seleccionada entre las fechas especificadas.
                        <br/><br/>
                        **Nuevo CPC:** ${newCpc}
                        <br/><br/>
                        Esta acción es irreversible. ¿Estás seguro de que quieres continuar?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleConfirmUpdate} color="warning" variant="contained">Sí, Actualizar</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default UpdateCostPage;
